import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  TextField,
  Alert,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { formatDisplayAmount } from '../functions/format_display_amount.function';

const ALL_CATEGORY_OPTIONS = [
  'OWN-Fiber 1st 30%',
  'OWN-Fiber 2nd 30%',
  'OWN-Fiber 3rd 30%',
  'Own Fiber 4th 10%',
  'IRU-On-Net 1st 40%',
  'IRU-On-Net 2nd 30%',
  'IRU-On-Net 3rd 30%',
  'IRU-Off-Net 1st 70%',
  'IRU-Off-Net 2nd 30%',
  'FTTS-On-Net 1st 40%',
  'FTTS-On-net 2nd 30%',
  'FTTS-On-net 3rd 30%',
  'FTTS-Off-Net 1st 70%',
  'FTTS-Off-Net 2nd 30%',
  'Fiber Delta Scope',
  'Others',
];

function extractPercentage(categoryName) {
  if (categoryName === 'Fiber Delta Scope' || categoryName === 'Others') {
    return null;
  }
  const match = categoryName.match(/(\d+)%/);
  return match ? parseInt(match[1], 10) : null;
}

const isOthersCategory = (category) => category === 'Others';

const parseMilestonePct = (value) => {
  const cleaned = String(value || '')
    .replace(/%/g, '')
    .trim();
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const formatMilestoneInput = (raw) => {
  const trimmed = String(raw || '').trim();
  if (!trimmed) {
    return '';
  }
  const numPart = trimmed.replace(/%/g, '').trim();
  if (!numPart || !/^\d*\.?\d+$/.test(numPart)) {
    return trimmed;
  }
  return trimmed.includes('%') ? `${numPart}%` : numPart;
};

const formatMilestonePctValue = (value) => {
  const n = parseMilestonePct(value);
  if (!Number.isFinite(n)) {
    return '0';
  }
  return String(parseFloat(n.toFixed(3)));
};

const capMilestoneToMax = (raw, maxRemaining) => {
  const trimmed = String(raw || '').trim();
  if (!trimmed) {
    return '';
  }

  const max = Math.max(0, Number(maxRemaining) || 0);
  const hasPercent = trimmed.includes('%');
  const numPart = trimmed.replace(/%/g, '').trim();

  if (!numPart || !/^\d*\.?\d*$/.test(numPart)) {
    return trimmed;
  }

  if (numPart.endsWith('.')) {
    const partial = parseFloat(numPart);
    if (Number.isFinite(partial) && partial > max) {
      const capped = formatMilestonePctValue(max);
      return hasPercent ? `${capped}%` : capped;
    }
    return trimmed;
  }

  const parsed = parseFloat(numPart);
  if (!Number.isFinite(parsed)) {
    return trimmed;
  }

  if (parsed > max) {
    const capped = formatMilestonePctValue(max);
    return hasPercent ? `${capped}%` : capped;
  }

  return formatMilestoneInput(trimmed);
};

function inferFamilyFromContext(invoiceType, type, invoiceNumber) {
  const invType = String(invoiceType || '').toLowerCase();
  if (invType === 'ftts') {
    return 'FTTS';
  }

  // Non-FTTS: the Type dropdown is explicit (Own-fiber / IRU-Onnet / IRU-OffNet)
  const t = String(type || '').toLowerCase();
  if (t.includes('own')) {
    return 'OWN';
  }
  if (t.includes('iru')) {
    return 'IRU';
  }

  // Fallback: try invoice number text if present
  const num = String(invoiceNumber || '').toLowerCase();
  if (num.includes('own')) {
    return 'OWN';
  }
  if (num.includes('iru')) {
    return 'IRU';
  }
  return null;
}

function inferNetFromType(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('off-net') || t.includes('off net') || t.includes('offnet')) {
    return 'OFF';
  }
  if (t.includes('on-net') || t.includes('on net') || t.includes('onnet')) {
    return 'ON';
  }
  return null;
}

const CATEGORY_STEPS = {
  OWN: {
    ON: ['OWN-Fiber 1st 30%', 'OWN-Fiber 2nd 30%', 'OWN-Fiber 3rd 30%', 'Own Fiber 4th 10%'],
    OFF: [],
  },
  IRU: {
    ON: ['IRU-On-Net 1st 40%', 'IRU-On-Net 2nd 30%', 'IRU-On-Net 3rd 30%'],
    OFF: ['IRU-Off-Net 1st 70%', 'IRU-Off-Net 2nd 30%'],
  },
  FTTS: {
    ON: ['FTTS-On-Net 1st 40%', 'FTTS-On-net 2nd 30%', 'FTTS-On-net 3rd 30%'],
    OFF: ['FTTS-Off-Net 1st 70%', 'FTTS-Off-Net 2nd 30%'],
  },
};

function getSequentialCategoryOptions({ family, net, coveredPct }) {
  const steps = CATEGORY_STEPS?.[family]?.[net] || [];
  if (!steps.length) {
    return [];
  }
  if (coveredPct >= 100) {
    return [];
  }

  const stepPcts = steps.map((s) => extractPercentage(s) || 0);
  // Strict sequential: pick the next step after the largest cumulative <= coveredPct
  let cum = 0;
  let nextIdx = 0;
  for (let i = 0; i < stepPcts.length; i++) {
    if (coveredPct <= cum) {
      nextIdx = i;
      break;
    }
    cum += stepPcts[i];
    nextIdx = i + 1;
  }
  if (nextIdx >= 0 && nextIdx < steps.length) {
    return [steps[nextIdx]];
  }
  return [];
}

const RingDetailsSection = ({
  rings,
  onRingChange,
  onAddRing,
  onRemoveRing,
  invoiceType,
  invoiceNumber,
  amountMismatchError,
  ringDetailsData = [],
  poUnitPrice = '',
  poVerificationData = null,
  type = '',
}) => {
  const ringNameOptions = React.useMemo(() => {
    const names = (ringDetailsData || [])
      .map((r) =>
        r.ring_unique_name != null && r.ring_unique_name !== ''
          ? String(r.ring_unique_name).trim()
          : null
      )
      .filter(Boolean);
    return [...new Set(names)];
  }, [ringDetailsData]);

  const firstFieldLabel = invoiceType === 'Non-FTTS' ? 'Section Name' : 'Ring name';
  const fieldName = invoiceType === 'Non-FTTS' ? 'sectionName' : 'ringName';
  const sectionHeading = invoiceType === 'Non-FTTS' ? 'Section Details' : 'Ring Details';
  const coverageLabel = invoiceType === 'Non-FTTS' ? 'Section coverage' : 'Ring coverage';

  const isTypeSelected = !!(type && String(type).trim());

  const totalCoveredMilestones = React.useMemo(() => {
    if (!poVerificationData) {
      return undefined;
    }
    const poData = poVerificationData.response || poVerificationData;
    return poData.totalCoveredMilestones !== undefined ? poData.totalCoveredMilestones : undefined;
  }, [poVerificationData]);

  // Per-ring coverage based on invoices already created against this PO.
  // verify-po now returns `rings_data` (raw per-invoice arrays) in the same response object.
  const coveredPctByRingName = React.useMemo(() => {
    const poData = poVerificationData?.response || poVerificationData;
    const invoicesRaw = Array.isArray(poData?.rings_data) ? poData.rings_data : [];
    const invoices = invoicesRaw.filter(
      (inv) =>
        String(inv?.status || '')
          .toLowerCase()
          .trim() !== 'rejected'
    );
    const map = new Map();

    const parsePct = (v) => parseMilestonePct(v);

    invoices.forEach((inv) => {
      const ringsArr = Array.isArray(inv?.rings_data) ? inv.rings_data : [];
      ringsArr.forEach((r) => {
        const nameRaw = r?.ring_unique_name || r?.ringName || r?.sectionName || '';
        const name = String(nameRaw || '').trim();
        if (!name) {
          return;
        }
        const pct = parsePct(r?.paymentMilestone || r?.paymentRingMilestone);
        if (!pct) {
          return;
        }
        map.set(name, (map.get(name) || 0) + pct);
      });
    });

    // cap at 100
    const obj = {};
    map.forEach((v, k) => {
      obj[k] = Math.min(100, Math.max(0, Number(v) || 0));
    });
    return obj;
  }, [poVerificationData]);

  // Prevent selecting the same ring multiple times across rows.
  const getAvailableRingOptionsForRow = React.useCallback(
    (rowIndex) => {
      const selected = new Set(
        (rings || [])
          .map((r, idx) => {
            if (idx === rowIndex) {
              return null;
            }
            const name = r?.ringName || r?.sectionName || '';
            return String(name || '').trim() || null;
          })
          .filter(Boolean)
      );
      return ringNameOptions.map((name) => {
        const n = String(name).trim();
        const alreadySelected = selected.has(n);
        const covered = coveredPctByRingName[n] || 0;
        const fullyCovered = covered >= 100;
        return { name: n, disabled: alreadySelected || fullyCovered, covered };
      });
    },
    [rings, ringNameOptions, coveredPctByRingName]
  );

  const getRingCoveredPct = (ring) => {
    const name = String(ring?.ringName || ring?.sectionName || '').trim();
    if (!name) {
      return 0;
    }
    return coveredPctByRingName[name] || 0;
  };

  const getRemainingPctForRing = (ring) => Math.max(0, 100 - getRingCoveredPct(ring));

  const getFilteredCategoryOptionsForRing = (ring) => {
    const covered = getRingCoveredPct(ring);
    const family = inferFamilyFromContext(invoiceType, type, invoiceNumber);
    let net = inferNetFromType(type);
    // Non-FTTS "Own-fiber" doesn't carry net; treat it as ON (OWN milestones are defined under ON)
    if (!net && family === 'OWN') {
      net = 'ON';
    }

    // Always allow these
    const misc = ['Fiber Delta Scope', 'Others'];

    if (!family || !net) {
      // If we can't infer, don't show cross-family options; just misc.
      return misc;
    }

    const seq = getSequentialCategoryOptions({ family, net, coveredPct: covered });
    return [...seq, ...misc];
  };

  const filteredCategoryOptions = React.useMemo(() => {
    if (totalCoveredMilestones === undefined) {
      return ALL_CATEGORY_OPTIONS;
    }
    const remaining = 100 - (totalCoveredMilestones || 0);
    return ALL_CATEGORY_OPTIONS.filter((category) => {
      const percentage = extractPercentage(category);
      if (percentage === null) {
        return category === 'Fiber Delta Scope' || category === 'Others';
      }
      return percentage <= remaining;
    });
  }, [totalCoveredMilestones]);

  const getMilestoneOptionsForCategory = (category, remainingPctForRing) => {
    const percentage = extractPercentage(category);
    if (percentage !== null) {
      return [`${percentage}%`];
    }
    const remaining = Math.max(0, Math.min(100, Number(remainingPctForRing) || 0));
    return Array.from({ length: remaining }, (_, i) => `${i + 1}%`);
  };

  // Set length from selected ring: total on_net_meters if type is on-net, total off_net_meters if type is off-net
  const computeLengthFromRingAndType = React.useCallback(
    (index) => {
      const ring = rings[index];
      const ringName = ring?.ringName || ring?.sectionName || '';
      if (!ringName || !ringDetailsData?.length) {
        return;
      }
      const typeNorm = (type || '').toLowerCase();
      const isNonFtts = invoiceType === 'Non-FTTS';
      const useOnNet = typeNorm.includes('on-net') || typeNorm === 'on-net';
      const useOffNet = typeNorm.includes('off-net') || typeNorm === 'off-net';
      // For Non-FTTS (Section Details) we always use total_length_meters.
      // For FTTS we require an on-net/off-net type selection to know which value to use.
      if (!isNonFtts && !useOnNet && !useOffNet) {
        return;
      }
      const match = ringDetailsData.find(
        (r) =>
          r.ring_unique_name != null &&
          String(r.ring_unique_name).trim() === String(ringName).trim()
      );
      if (!match) {
        return;
      }
      const meterVal = isNonFtts
        ? parseFloat(String(match.total_length_meters || 0).replace(/,/g, ''), 10)
        : useOnNet
          ? parseFloat(String(match.on_net_meters || 0).replace(/,/g, ''), 10)
          : parseFloat(String(match.off_net_meters || 0).replace(/,/g, ''), 10);
      if (Number.isNaN(meterVal)) {
        return;
      }
      const lengthStr = meterVal % 1 === 0 ? String(meterVal) : meterVal.toFixed(2);
      onRingChange(index, 'length', lengthStr);
    },
    [rings, ringDetailsData, type, onRingChange, invoiceType]
  );

  const ringInputsSignature = React.useMemo(
    () =>
      rings
        .map(
          (r) =>
            `${r.ringName || r.sectionName || ''}|${r.paymentRingMilestone || r.paymentMilestone || ''}`
        )
        .join(';'),
    [rings]
  );

  // When ring name or type changes: set length = total on_net_meters (if on-net) or total off_net_meters (if off-net).
  // Only depend on type, ringDetailsData, ringInputsSignature so we do NOT overwrite length when user manually edits it
  // (onRingChange is not in deps because parent often passes a new ref each render, which would re-run this and overwrite user input).
  const onRingChangeRef = React.useRef(onRingChange);
  React.useEffect(() => {
    onRingChangeRef.current = onRingChange;
  }, [onRingChange]);

  React.useEffect(() => {
    rings.forEach((ring, index) => {
      const ringName = ring?.ringName || ring?.sectionName || '';
      const typeNorm = (type || '').toLowerCase();
      const isNonFtts = invoiceType === 'Non-FTTS';
      const useOnNet = typeNorm.includes('on-net') || typeNorm === 'on-net';
      const useOffNet = typeNorm.includes('off-net') || typeNorm === 'off-net';
      if (!ringName || (!isNonFtts && !useOnNet && !useOffNet) || !ringDetailsData?.length) {
        return;
      }
      const match = ringDetailsData.find(
        (r) =>
          r.ring_unique_name != null &&
          String(r.ring_unique_name).trim() === String(ringName).trim()
      );
      if (!match) {
        return;
      }
      const meterVal = isNonFtts
        ? parseFloat(String(match.total_length_meters || 0).replace(/,/g, ''), 10)
        : useOnNet
          ? parseFloat(String(match.on_net_meters || 0).replace(/,/g, ''), 10)
          : parseFloat(String(match.off_net_meters || 0).replace(/,/g, ''), 10);
      if (Number.isNaN(meterVal)) {
        return;
      }
      const lengthStr = meterVal % 1 === 0 ? String(meterVal) : meterVal.toFixed(2);
      const current = ring.length ? String(ring.length).replace(/,/g, '') : '';
      if (current !== lengthStr) {
        onRingChangeRef.current(index, 'length', lengthStr);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only set default length when ring/type change, not when user edits length
  }, [type, ringDetailsData, ringInputsSignature, invoiceType]);

  const handleCategoryChange = (index, value) => {
    onRingChange(index, 'category', value);
    const percentage = extractPercentage(value);
    if (percentage !== null) {
      const lockedValue = `${percentage}%`;
      onRingChange(index, 'paymentRingMilestone', lockedValue);
      setTimeout(() => computeLengthFromRingAndType(index), 0);
    } else {
      onRingChange(index, 'paymentRingMilestone', '');
    }
  };

  const handlePaymentMilestoneChange = (index, value) => {
    onRingChange(index, 'paymentRingMilestone', value || '');
    if (value) {
      setTimeout(() => computeLengthFromRingAndType(index), 0);
    }
  };

  const handleOthersMilestoneInput = (index, value, maxRemaining) => {
    onRingChange(index, 'paymentRingMilestone', capMilestoneToMax(value, maxRemaining));
  };

  const handleOthersMilestoneBlur = (index, value, maxRemaining) => {
    const capped = capMilestoneToMax(value, maxRemaining);
    const formatted = formatMilestoneInput(capped);
    const numPart = formatted.replace(/%/g, '').trim();
    if (numPart && /^\d*\.?\d+$/.test(numPart) && !formatted.includes('%')) {
      onRingChange(index, 'paymentRingMilestone', `${numPart}%`);
    } else {
      onRingChange(index, 'paymentRingMilestone', formatted);
    }
    if (value) {
      setTimeout(() => computeLengthFromRingAndType(index), 0);
    }
  };

  const handleOthersMilestoneSelect = (index, value, maxRemaining) => {
    const capped = capMilestoneToMax(value, maxRemaining);
    handlePaymentMilestoneChange(index, capped);
  };

  const renderOthersMilestoneField = (ring, index) => {
    const remainingPct = getRemainingPctForRing(ring);
    const remainingLabel = formatMilestonePctValue(remainingPct);

    return (
      <Autocomplete
        freeSolo
        fullWidth
        size="small"
        disabled={!isTypeSelected}
        options={getMilestoneOptionsForCategory(ring.category || '', remainingPct)}
        value={ring.paymentRingMilestone || ring.paymentMilestone || ''}
        onChange={(_, newValue) => handleOthersMilestoneSelect(index, newValue || '', remainingPct)}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === 'input') {
            handleOthersMilestoneInput(index, newInputValue, remainingPct);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Payment Milestone"
            placeholder={`e.g. 3.158% (max ${remainingLabel}%)`}
            helperText={`Max: ${remainingLabel}%`}
            FormHelperTextProps={{ sx: { margin: 0, height: '20px' } }}
            onBlur={(e) =>
              handleOthersMilestoneBlur(
                index,
                e.target.value || ring.paymentRingMilestone || '',
                remainingPct
              )
            }
          />
        )}
      />
    );
  };

  // Max length for a ring row (on_net_meters or off_net_meters from PO data based on type). Null if no ring/type.
  const getMaxLengthForRing = React.useCallback(
    (index) => {
      const ring = rings[index];
      const ringName = ring?.ringName || ring?.sectionName || '';
      const typeNorm = (type || '').toLowerCase();
      const isNonFtts = invoiceType === 'Non-FTTS';
      const useOnNet = typeNorm.includes('on-net') || typeNorm === 'on-net';
      const useOffNet = typeNorm.includes('off-net') || typeNorm === 'off-net';
      if (!ringName || (!isNonFtts && !useOnNet && !useOffNet) || !ringDetailsData?.length) {
        return null;
      }
      const match = ringDetailsData.find(
        (r) =>
          r.ring_unique_name != null &&
          String(r.ring_unique_name).trim() === String(ringName).trim()
      );
      if (!match) {
        return null;
      }
      const meterVal = isNonFtts
        ? parseFloat(String(match.total_length_meters || 0).replace(/,/g, ''), 10)
        : useOnNet
          ? parseFloat(String(match.on_net_meters || 0).replace(/,/g, ''), 10)
          : parseFloat(String(match.off_net_meters || 0).replace(/,/g, ''), 10);
      return Number.isNaN(meterVal) ? null : meterVal;
    },
    [rings, type, ringDetailsData, invoiceType]
  );

  const handleLengthChange = (index, rawValue) => {
    const max = getMaxLengthForRing(index);
    if (rawValue === '' || rawValue == null) {
      onRingChange(index, 'length', rawValue === '' ? '' : rawValue);
      return;
    }
    const num = parseFloat(String(rawValue).replace(/,/g, ''));
    if (!Number.isNaN(num) && max != null && num > max) {
      const capped = max % 1 === 0 ? String(max) : max.toFixed(2);
      onRingChange(index, 'length', capped);
      return;
    }
    onRingChange(index, 'length', rawValue);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        sx={{
          fontSize: '18px',
          fontFamily: 'Inter',
          fontWeight: 600,
          mb: 2,
          display: 'flex',
        }}
      >
        {sectionHeading}
      </Typography>
      {/* comment temporary */}
      {/* Read-only aggregated ring data from PO */}
      {/* {ringDetailsData.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxWidth: '96%' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>PO Number</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Ring Unique Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Total Length Mtrs
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  On Net Mtrs
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Off Net Mtrs
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ringDetailsData.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell>{row.po_number ?? '—'}</TableCell>
                  <TableCell>{row.ring_unique_name ?? '—'}</TableCell>
                  <TableCell align="right">{row.total_length_meters ?? '—'}</TableCell>
                  <TableCell align="right">{row.on_net_meters ?? '—'}</TableCell>
                  <TableCell align="right">{row.off_net_meters ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )} */}

      {rings.map((ring, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 2 }} alignItems="flex-start">
          {(() => {
            const covered = getRingCoveredPct(ring);
            const remaining = Math.max(0, 100 - covered);
            const ringName = String(ring?.ringName || ring?.sectionName || '').trim();
            return (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    p: 1.25,
                    borderRadius: 1,
                    border: '1px solid #eaecf0',
                    bgcolor: '#fafafa',
                  }}
                >
                  <Box sx={{ minWidth: 220 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1D242E' }}>
                      {ringName ? `Coverage for ${ringName}` : coverageLabel}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#667085' }}>
                      Covered: {covered}% • Remaining: {remaining}%
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, maxWidth: 520 }}>
                    <LinearProgress
                      variant="determinate"
                      value={covered}
                      sx={{
                        height: 10,
                        borderRadius: 999,
                        bgcolor: '#eef2f6',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 999,
                          bgcolor: covered >= 100 ? '#2e7d32' : '#1976d2',
                        },
                      }}
                    />
                  </Box>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={covered >= 100 ? 'Fully covered' : `${remaining}% left`}
                    sx={{ bgcolor: '#fff' }}
                  />
                </Box>
              </Grid>
            );
          })()}
          <Grid item xs={12} sm={5.5} md={1.83}>
            <FormControl fullWidth size="small" disabled={!isTypeSelected}>
              <InputLabel>{firstFieldLabel}</InputLabel>
              <Select
                label={firstFieldLabel}
                value={ring[fieldName] || ring.ringName || ring.sectionName || ''}
                onChange={(e) => onRingChange(index, fieldName, e.target.value)}
                disabled={!isTypeSelected}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'left',
                  },
                }}
              >
                <MenuItem value="">
                  <em>{invoiceType === 'Non-FTTS' ? 'Select section' : 'Select ring'}</em>
                </MenuItem>
                {getAvailableRingOptionsForRow(index).map((opt) => (
                  <MenuItem
                    key={opt.name}
                    value={opt.name}
                    disabled={opt.disabled}
                    sx={opt.disabled ? { opacity: 0.45, filter: 'grayscale(1)' } : undefined}
                  >
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5.5} md={1.83}>
            {getRingCoveredPct(ring) >= 100 ? (
              <TextField
                fullWidth
                label="Category"
                value="Fully covered"
                size="small"
                disabled
                sx={{ opacity: 0.6 }}
                helperText=" "
                FormHelperTextProps={{ sx: { margin: 0, height: '20px' } }}
              />
            ) : (
              <FormControl fullWidth size="small" disabled={!isTypeSelected}>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={ring.category || ''}
                  onChange={(e) => handleCategoryChange(index, e.target.value)}
                  disabled={!isTypeSelected}
                  sx={{ '& .MuiSelect-select': { textAlign: 'left' } }}
                >
                  <MenuItem value="">
                    <em>Select category</em>
                  </MenuItem>
                  {getFilteredCategoryOptionsForRing(ring).map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12} sm={5.5} md={1.83}>
            {getRingCoveredPct(ring) >= 100 ? (
              <TextField
                fullWidth
                label="Payment Milestone"
                value="—"
                size="small"
                disabled
                sx={{ opacity: 0.6 }}
                helperText=" "
                FormHelperTextProps={{ sx: { margin: 0, height: '20px' } }}
              />
            ) : isOthersCategory(ring.category) ? (
              renderOthersMilestoneField(ring, index)
            ) : (
              <FormControl fullWidth size="small" disabled={!isTypeSelected}>
                <InputLabel>Payment Milestone</InputLabel>
                <Select
                  label="Payment Milestone"
                  value={ring.paymentRingMilestone || ring.paymentMilestone || ''}
                  onChange={(e) => handlePaymentMilestoneChange(index, e.target.value)}
                  disabled={!isTypeSelected || extractPercentage(ring.category || '') !== null}
                  sx={{ '& .MuiSelect-select': { textAlign: 'left' } }}
                >
                  <MenuItem value="">
                    <em>Select %</em>
                  </MenuItem>
                  {(() => {
                    const remaining = Math.max(0, 100 - getRingCoveredPct(ring));
                    return getMilestoneOptionsForCategory(ring.category || '', remaining).map(
                      (opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      )
                    );
                  })()}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12} sm={5.5} md={1.83}>
            <TextField
              fullWidth
              label="Unit Rate"
              placeholder="unit rate"
              value={ring.unitRate || poUnitPrice || ''}
              onChange={(e) => onRingChange(index, 'unitRate', e.target.value)}
              size="small"
              disabled={!!poUnitPrice || !isTypeSelected}
              helperText=" "
              FormHelperTextProps={{ sx: { margin: 0, height: '20px' } }}
              sx={
                poUnitPrice
                  ? {
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                      },
                    }
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12} sm={5.5} md={1.83}>
            <TextField
              fullWidth
              label="Length"
              placeholder="..."
              value={ring.length}
              onChange={(e) => handleLengthChange(index, e.target.value)}
              size="small"
              helperText={
                getMaxLengthForRing(index) != null ? `Max: ${getMaxLengthForRing(index)} m` : ' '
              }
              error={(() => {
                const max = getMaxLengthForRing(index);
                if (max == null) {
                  return false;
                }
                const current = parseFloat(String(ring.length || '').replace(/,/g, ''));
                return !Number.isNaN(current) && current > max;
              })()}
              FormHelperTextProps={{ sx: { margin: 0, height: '20px' } }}
            />
          </Grid>
          <Grid item xs={12} sm={5.5} md={1.83}>
            {(() => {
              const hasRingSelected = Boolean(
                String(ring?.ringName || ring?.sectionName || '').trim()
              );
              const isAmountDisabled = !isTypeSelected || hasRingSelected;
              return (
                <TextField
                  fullWidth
                  label="Amount"
                  placeholder="Auto-calculated"
                  value={isAmountDisabled ? formatDisplayAmount(ring.amount) : ring.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^[\d,.]*$/.test(value)) {
                      onRingChange(index, 'amount', value);
                    }
                  }}
                  size="small"
                  disabled={isAmountDisabled}
                  error={amountMismatchError?.hasError || false}
                  helperText={
                    amountMismatchError?.hasError && amountMismatchError?.invalidInput
                      ? 'Invalid amount'
                      : amountMismatchError?.hasError && !amountMismatchError?.invalidInput
                        ? 'The amount sum does not match the total invoice amount'
                        : ' '
                  }
                  FormHelperTextProps={{ sx: { margin: 0, height: '20px' } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-error fieldset': {
                        borderColor: '#d32f2f',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fff',
                      },
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                      color: 'rgba(0, 0, 0, 0.87)',
                      opacity: 1,
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              );
            })()}
          </Grid>
          <Grid
            item
            xs={12}
            sm={1}
            md={0.5}
            sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
          >
            {index === 0 ? (
              <IconButton
                onClick={onAddRing}
                disabled={!isTypeSelected}
                sx={{
                  color: '#4caf50',
                  padding: '8px',
                  '&:hover': {
                    color: '#2e7d32',
                    backgroundColor: 'rgba(76, 175, 80, 0.08)',
                  },
                }}
              >
                <AddCircleOutline sx={{ width: '20px', height: '20px' }} />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => onRemoveRing(index)}
                disabled={!isTypeSelected}
                sx={{
                  color: '#d32f2f',
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                  },
                }}
              >
                <RemoveCircleOutline sx={{ width: '20px', height: '20px' }} />
              </IconButton>
            )}
          </Grid>
        </Grid>
      ))}
      <Button
        startIcon={<AddCircleOutline sx={{ width: '12px', height: '12px' }} />}
        onClick={onAddRing}
        disabled={!isTypeSelected}
        sx={{
          color: '#A1A1AA',
          display: 'flex',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'rgba(211, 47, 47, 0.04)',
          },
        }}
      >
        Add
      </Button>

      {/* Amount Mismatch Error Message */}
      {amountMismatchError?.hasError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {amountMismatchError.invalidInput
            ? amountMismatchError.message
            : 'The amount sum does not match the total invoice amount'}
        </Alert>
      )}
    </Box>
  );
};

export default RingDetailsSection;
